import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/lib/auth-client';
import { useTicket } from '@/hooks/tickets';
import { useReplies } from '@/hooks/replies';
import { TicketHeader } from '@/components/tickets/TicketHeader';
import { TicketMeta } from '@/components/tickets/TicketMeta';
import { TicketBody } from '@/components/tickets/TicketBody';
import { ReplyThread } from '@/components/tickets/ReplyThread';
import { ReplyForm } from '@/components/tickets/ReplyForm';
import { TicketSidebar } from '@/components/tickets/TicketSidebar';

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: session } = useSession();
  const isAdmin = session?.user.role === 'ADMIN';

  const { data, isLoading, isError } = useTicket(id);
  const { data: replies } = useReplies(id);

  const canEdit =
    isAdmin || (!!session?.user.id && session.user.id === data?.assignedAgentId);

  return (
    <div className='max-w-5xl mx-auto px-4 py-8'>
      <Link
        to='/tickets'
        className='inline-flex items-center gap-1.5 text-xs font-medium mb-6 transition-colors'
        style={{ color: 'var(--muted-foreground)' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--primary)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted-foreground)'; }}
      >
        <ArrowLeft className='h-3.5 w-3.5' />
        Back to tickets
      </Link>

      {isLoading && (
        <div className='space-y-4'>
          <Skeleton className='h-7 w-2/3' />
          <Skeleton className='h-4 w-1/3' />
          <Skeleton className='h-32 w-full mt-6' />
        </div>
      )}

      {isError && (
        <p className='text-sm text-destructive'>
          Failed to load ticket. It may not exist.
        </p>
      )}

      {data && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-8 items-start'>
          <div className='md:col-span-2 space-y-6'>
            <TicketHeader
              id={data.id}
              status={data.status}
              category={data.category}
              subject={data.subject}
            />
            <TicketMeta
              fromName={data.fromName}
              fromEmail={data.fromEmail}
              createdAt={data.createdAt}
            />
            <TicketBody
              ticketId={id!}
              body={data.body}
              aiSummary={data.aiSummary}
              suggestedReply={data.suggestedReply}
            />
            <ReplyThread replies={replies ?? []} />
            <ReplyForm ticketId={id!} />
          </div>

          <TicketSidebar ticket={data} canEdit={canEdit} isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
}
